import * as Sentry from '@sentry/react';

export const isSentryEnabled = () => {
  // vite.config defines VITE_IS_BUILD as 'true' | 'false' during build; be defensive for both
  const isBuildFlag = import.meta.env.VITE_IS_BUILD;
  const isProductionBuild = isBuildFlag === 'true' || isBuildFlag === true;
  return isProductionBuild || import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true';
};

export const sentryUtils = {
  setUser: (user: { id: string; email?: string; username?: string; role?: string }) => {
    if (!isSentryEnabled()) return;
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      role: user.role,
    });
  },

  clearUser: () => {
    if (!isSentryEnabled()) return;
    Sentry.setUser(null);
  },

  setTag: (key: string, value: string) => {
    if (!isSentryEnabled()) return;
    Sentry.setTag(key, value);
  },

  setContext: (key: string, context: Record<string, unknown>) => {
    if (!isSentryEnabled()) return;
    Sentry.setContext(key, context);
  },

  addBreadcrumb: (message: string, category?: string, level?: Sentry.SeverityLevel, data?: Record<string, unknown>) => {
    if (!isSentryEnabled()) return;
    Sentry.addBreadcrumb({
      message,
      category: category || 'user-action',
      level: level || 'info',
      data,
      timestamp: Date.now() / 1000,
    });
  },

  captureMessage: (message: string, level: Sentry.SeverityLevel = 'info') => {
    if (!isSentryEnabled()) return;
    return Sentry.captureMessage(message, level);
  },

  captureException: (error: Error, context?: Record<string, unknown>) => {
    if (!isSentryEnabled()) return;
    return Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value as Record<string, unknown>);
        });
      }
      return Sentry.captureException(error);
    });
  },

  captureEvent: (event: Partial<Sentry.Event>) => {
    if (!isSentryEnabled()) return;
    return Sentry.captureEvent(event);
  },

  withErrorBoundary: <P extends object>(Component: React.ComponentType<P>) => {
    if (!isSentryEnabled()) return Component;
    return Sentry.withErrorBoundary(Component, {
      showDialog: true,
    });
  },

  startSpan: <T>(options: { name: string; op?: string }, callback: () => T) => {
    if (!isSentryEnabled()) return callback();
    return Sentry.startSpan({
      name: options.name,
      op: options.op || 'navigation',
    }, callback);
  },

  withScope: (callback: (scope: Sentry.Scope) => void) => {
    if (!isSentryEnabled()) return;
    Sentry.withScope(callback);
  },
};

export const trackUserAction = (action: string, data?: Record<string, unknown>) => {
  sentryUtils.addBreadcrumb(`User ${action}`, 'user-action', 'info', data);
};

export const trackAPICall = (endpoint: string, method: string, status?: number, duration?: number) => {
  sentryUtils.addBreadcrumb(
    `API ${method} ${endpoint}`,
    'http',
    status && status >= 400 ? 'error' : 'info',
    { method, endpoint, status, duration }
  );
};

export const captureAPIError = (
  error: Error,
  requestData?: {
    url?: string;
    method?: string;
    headers?: Record<string, string>;
    data?: unknown;
    params?: Record<string, unknown>;
  },
  responseData?: {
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
    data?: unknown;
  }
) => {
  if (!isSentryEnabled()) return;
  return Sentry.withScope((scope) => {
    if (requestData) {
      scope.setContext('request_details', requestData);
      scope.setTag('api_endpoint', requestData.url || 'unknown');
      scope.setTag('api_method', requestData.method || 'unknown');
    }
    
    if (responseData) {
      scope.setContext('response_details', responseData);
      scope.setTag('api_status', responseData.status || 'unknown');
    }
    
    scope.setTag('error_type', 'api_error');
    return Sentry.captureException(error);
  });
};

export const trackNavigation = (from: string, to: string) => {
  sentryUtils.addBreadcrumb(`Navigation from ${from} to ${to}`, 'navigation', 'info', { from, to });
};

export const measurePerformance = async <T>(
  operationName: string,
  operation: () => Promise<T>
): Promise<T> => {
  return sentryUtils.startSpan({ name: operationName, op: 'function' }, operation) as Promise<T>;
};

// Feedback utilities
export const feedbackUtils = {
  /**
   * Open the Sentry User Feedback dialog (Sentry v8+/v10 API).
   * Uses createForm → appendToDom → open.
   */
  showFeedbackDialog: async () => {
    if (!isSentryEnabled()) return false;

    const feedback = Sentry.getFeedback();
    if (!feedback || typeof feedback.createForm !== 'function') {
      console.warn('[sentry] Feedback integration is not available');
      return false;
    }

    try {
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
      return true;
    } catch (error) {
      console.error('[sentry] Failed to open feedback dialog:', error);
      return false;
    }
  },

  // Create a feedback with context
  createFeedback: (message: string, name?: string, email?: string, url?: string) => {
    if (!isSentryEnabled()) return;
    // Prefer the public sendFeedback API when available
    if (typeof Sentry.captureFeedback === 'function') {
      Sentry.captureFeedback({
        message,
        name: name || 'Anonymous',
        email: email || '',
        url: url || window.location.href,
      });
      return;
    }

    const feedbackIntegration = Sentry.getClient()?.getIntegrationByName?.('Feedback') as
      | { sendFeedback?: (data: unknown) => void }
      | undefined;
    feedbackIntegration?.sendFeedback?.({
      message,
      name: name || 'Anonymous',
      email: email || '',
      url: url || window.location.href,
    });
  },

  // Capture feedback with additional context
  captureFeedbackWithContext: (
    message: string, 
    options?: {
      name?: string;
      email?: string;
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    }
  ) => {
    if (!isSentryEnabled()) return;
    Sentry.withScope((scope) => {
      if (options?.tags) {
        Object.entries(options.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      
      if (options?.extra) {
        Object.entries(options.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      feedbackUtils.createFeedback(
        message,
        options?.name,
        options?.email,
        window.location.href
      );
    });
  },
};